import { RefObject } from 'react';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Legend from '@arcgis/core/widgets/Legend';
import Home from '@arcgis/core/widgets/Home';
import Viewpoint from '@arcgis/core/Viewpoint';
import TileInfo from '@arcgis/core/layers/support/TileInfo';

// Store
import store from '../store/store';
import { LEVEL_ALL, LevelSelectedType } from '../store/slices/admin2EstimatesSlice';
import { setMapLayersUpdating, setMapViewReady } from '../store/slices/mapSlice';

// Utils
import { getAdminClassBreaksRenderer } from '../utils/mapRenderers.utils';
import { getAdmin1LabelingInfo, getAdmin2LabelingInfo } from '../utils/mapLabeling.utils';
import {
  getAdmin1BoundariesGraphics,
  getAdmin1Graphics,
  getAdmin1Value,
  getAdmin2AddedAttributes,
  getAdmin2BoundariesGraphics,
  getAdmin2FeaturesAttributes,
  getAdmin2FeaturesDataFiltered,
  getAdmin2Graphics,
  getFeatureLayerID,
  getSelectedSubLayerID,
  handleAdminBoundariesGraphics,
  reorderAdmin2ReferenceFeatureLayerToTop,
  reorderAdminBoundariesLayerToTop,
  reorderPopupMarkerGraphicsLayerToTop,
  setLayerUpdatingWatcher,
} from '../utils/map.utils';
import { getAdmin2ReferenceFeatureLayer } from '../utils/referenceLayers.utils';
import { setupPopupCloseHandler } from '../utils/popup.utils';
import {
  getAdmin1DefinitionExpressionValue,
  getAdmin2DefinitionExpressionValue,
  updateAdmin1ClientSideFeaturesLayerDefinitionExpression,
  updateAdmin2ClientSideFeaturesLayerDefinitionExpression,
} from '../utils/mapDefinitionExpression.utils';

// Configs
import {
  ADMIN1_BOUNDARIES_LAYER_ID,
  ADMIN1_REGION_ID_FIELD,
  ADMIN2_BOUNDARIES_LAYER_ID,
  ADMIN__5X5km,
  ADMIN__Admin1,
  ADMIN__Admin2,
  DHSREGEN,
  GO_TO_OPTIONS,
  HIGHLIGHT_COLOR,
  HIGHLIGHT_COLOR_OPACITY,
} from '../configs/map.configs';
import { ADDITIONAL_EXTERNAL_FIELDS } from '../configs/mapColor.configs';
import { createAdmin2PopupTemplate, createPopupTemplate, defaultPopupOptions } from '../configs/popup.config';
import { SurveySelectedType } from '../configs/types';
import {
  IndicatorType,
  NO_IMPROVED_WATER_PREMISES_FIELD_VALUE,
  NO_IMPROVED_WATER_PREMISES_FIELD_VALUE_BACKUP,
} from '../configs/indicators.config';

class MapController {
  map?: Map;
  mapView?: MapView;
  mapViewHomeWidget?: Home;
  countryMapImageLayer?: MapImageLayer;
  admin1BoundariesGraphicsLayer?: GraphicsLayer;
  admin1ClientSideFeaturesLayer?: FeatureLayer;
  admin2BoundariesGraphicsLayer?: GraphicsLayer;
  admin2ClientSideFeaturesLayer?: FeatureLayer;
  admin2ReferenceFeatureLayer?: FeatureLayer;
  effectsGraphicsLayer?: GraphicsLayer;
  effectsGroupLayer?: GroupLayer;
  popupMarkerGraphicsLayer?: GraphicsLayer;
  legend?: Legend;

  initializeMap = (mapViewDomRef: RefObject<HTMLDivElement>, mapLegendDomRef: RefObject<HTMLDivElement>) => {
    if (!mapViewDomRef.current) return;
    if (!mapLegendDomRef.current) return;

    this.map = new Map({
      // basemap: 'gray-vector',
    });

    this.mapView = new MapView({
      map: this.map,
      container: mapViewDomRef.current,
      popup: defaultPopupOptions,
      constraints: {
        lods: TileInfo.create().lods,
        minZoom: 5,
      },
    });

    // Add home widget to the top left corner of the mapView
    this.mapViewHomeWidget = new Home({ view: this.mapView });
    this.mapView.ui.add(this.mapViewHomeWidget, 'top-left');

    this.legend = new Legend({
      view: this.mapView,
      container: mapLegendDomRef.current,
    });

    this.admin1BoundariesGraphicsLayer = new GraphicsLayer({ id: ADMIN1_BOUNDARIES_LAYER_ID });
    this.map?.add(this.admin1BoundariesGraphicsLayer);

    this.admin2BoundariesGraphicsLayer = new GraphicsLayer({ id: ADMIN2_BOUNDARIES_LAYER_ID, visible: false });
    this.map?.add(this.admin2BoundariesGraphicsLayer);

    this.popupMarkerGraphicsLayer = new GraphicsLayer({ id: 'popupMarkerGraphicsLayer' });
    this.map?.add(this.popupMarkerGraphicsLayer);

    this.mapView.when((mapView: __esri.MapView) => {
      // Setup on click event listener to handle popup for raster layers
      // mapView.on('click', (event) => {
      //   if (!this.countryMapImageLayer) {
      //     return;
      //   }
      //   handleMapImageLayerPopup(event, mapView, this.countryMapImageLayer);
      // });

      // Setup on click event listener to handle removing popup marker on popup close
      if (this.popupMarkerGraphicsLayer) {
        setupPopupCloseHandler(mapView, this.popupMarkerGraphicsLayer);
      }

      store.dispatch(setMapViewReady(true));
    });
  };

  create_5x5_GroupLayer(surveySelected: SurveySelectedType) {
    this.countryMapImageLayer?.destroy();
    // this.effectsGraphicsLayer?.destroy();
    // this.effectsGroupLayer?.destroy();

    this.countryMapImageLayer = new MapImageLayer({
      id: 'countryMapImageLayer',
      title: surveySelected.DHSCC,
      url: surveySelected.rasterServiceURL,
      visible: false,
    });

    // this.effectsGraphicsLayer = new GraphicsLayer({
    //   id: `effectsGraphicsLayer_${surveySelected.DHSCC}`,
    //   blendMode: 'destination-in',
    // });
    //
    // this.effectsGroupLayer = new GroupLayer({
    //   id: 'effectsGroupLayer',
    //   layers: [this.countryMapImageLayer, this.effectsGraphicsLayer],
    // });

    // this.map?.add(this.effectsGroupLayer);
    this.map?.add(this.countryMapImageLayer);

    return this.countryMapImageLayer;
  }

  async setAdmin1Graphic(
    admin1FeaturesWithGeometries: any,
    indicatorsObject: any,
    adminSelected: string,
    surveySelected: SurveySelectedType
  ) {
    if (this.map && this.mapView) {
      store.dispatch(setMapLayersUpdating(true));
      const regionClientSideFeaturesLayerID = getFeatureLayerID(ADMIN__Admin1);
      const admin1LabelingInfo = getAdmin1LabelingInfo();
      const admin1Graphics = getAdmin1Graphics(admin1FeaturesWithGeometries, indicatorsObject);
      const popupTemplate = createPopupTemplate();

      if (this.admin1ClientSideFeaturesLayer) {
        this.admin1ClientSideFeaturesLayer?.destroy();
      }

      // Update Admin2 FeatureLayer definitionExpression
      const { levelSelected } = store.getState().admin2EstimatesSlice;
      const definitionExpression = getAdmin1DefinitionExpressionValue(levelSelected);

      this.admin1ClientSideFeaturesLayer = new FeatureLayer({
        id: regionClientSideFeaturesLayerID,
        // create an instance of esri/layers/support/Field for each field object
        labelingInfo: [admin1LabelingInfo], // Add the label class to the FeatureLayer
        fields: [...admin1FeaturesWithGeometries?.fields, ...ADDITIONAL_EXTERNAL_FIELDS],
        objectIdField: 'OBJECTID', // inferred from fields array if not specified
        geometryType: 'polygon', // geometryType and spatialReference are inferred from the first feature
        // in the source array if they are not specified.
        spatialReference: { wkid: 4326 },
        source: admin1Graphics, //  an array of graphics with geometry and attributes
        definitionExpression: definitionExpression,
        popupTemplate: popupTemplate,
        // a default simple renderer will be applied if not set.
        // UniqueValueRenderer based on `type` attribute
        visible: adminSelected === ADMIN__Admin1,
      });

      this.admin1ClientSideFeaturesLayer.renderer = await getAdminClassBreaksRenderer(
        this.admin1ClientSideFeaturesLayer
      );

      // Set watch on layerView updating for that layer
      setLayerUpdatingWatcher(this.mapView, this.admin1ClientSideFeaturesLayer);

      this.map.add(this.admin1ClientSideFeaturesLayer);

      if (this.admin1BoundariesGraphicsLayer) {
        const boundariesGraphics = getAdmin1BoundariesGraphics(admin1Graphics);
        await handleAdminBoundariesGraphics(
          this.admin1BoundariesGraphicsLayer,
          boundariesGraphics,
          surveySelected,
          ADMIN1_BOUNDARIES_LAYER_ID
        );
      }

      this.admin1ClientSideFeaturesLayer.when(() => {
        if (this.mapView && this.mapViewHomeWidget) {
          if (!this.mapView?.animation) {
            this.mapView?.goTo(this.admin1ClientSideFeaturesLayer?.fullExtent, GO_TO_OPTIONS);
          }

          this.mapViewHomeWidget.viewpoint = new Viewpoint({
            targetGeometry: this.admin1ClientSideFeaturesLayer?.fullExtent,
          });

          this.mapView.constraints.geometry = this.admin1ClientSideFeaturesLayer?.fullExtent;
        }
      });
    }
  }

  async setAdmin2Graphic(
    admin2FeaturesData: any,
    admin2FeaturesWithGeometries: any,
    indicatorID: string,
    surveySelected: SurveySelectedType | null
  ) {
    if (this.map && this.mapView) {
      store.dispatch(setMapLayersUpdating(true));
      const regionClientSideFeaturesLayerID = getFeatureLayerID(ADMIN__Admin2);
      const admin2LabelingInfo = getAdmin2LabelingInfo();
      const admin2Graphics = getAdmin2Graphics(
        admin2FeaturesData,
        admin2FeaturesWithGeometries,
        indicatorID,
        surveySelected
      );
      const popupTemplate = createAdmin2PopupTemplate();

      if (this.admin2ClientSideFeaturesLayer) {
        this.admin2ClientSideFeaturesLayer?.destroy();
      }

      const { levelSelected } = store.getState().admin2EstimatesSlice;
      const definitionExpression = getAdmin2DefinitionExpressionValue(levelSelected);

      this.admin2ClientSideFeaturesLayer = new FeatureLayer({
        id: regionClientSideFeaturesLayerID,
        // create an instance of esri/layers/support/Field for each field object
        labelingInfo: [admin2LabelingInfo], // Add the label class to the FeatureLayer
        fields: [...admin2FeaturesWithGeometries?.fields, ...ADDITIONAL_EXTERNAL_FIELDS],
        objectIdField: 'OBJECTID', // inferred from fields array if not specified
        geometryType: 'polygon', // geometryType and spatialReference are inferred from the first feature
        // in the source array if they are not specified.
        spatialReference: { wkid: 4326 },
        source: admin2Graphics, //  an array of graphics with geometry and attributes
        definitionExpression: definitionExpression,
        popupTemplate: popupTemplate,
        // a default simple renderer will be applied if not set.
        // UniqueValueRenderer based on `type` attribute
      });

      this.admin2ClientSideFeaturesLayer.renderer = await getAdminClassBreaksRenderer(
        this.admin2ClientSideFeaturesLayer
      );

      // Set watch on layerView updating for that layer
      setLayerUpdatingWatcher(this.mapView, this.admin2ClientSideFeaturesLayer);

      this.map.add(this.admin2ClientSideFeaturesLayer);

      if (this.admin2BoundariesGraphicsLayer && surveySelected) {
        const boundariesGraphics = await getAdmin2BoundariesGraphics(surveySelected);
        await handleAdminBoundariesGraphics(
          this.admin2BoundariesGraphicsLayer,
          boundariesGraphics,
          surveySelected,
          ADMIN2_BOUNDARIES_LAYER_ID
        );
      }
    }
  }

  async updateAdmin1Graphic(indicatorsObject: any) {
    if (this.map && this.mapView && this.admin1ClientSideFeaturesLayer) {
      const queryParams = {
        where: '1=1',
      };

      const { features } = await this.admin1ClientSideFeaturesLayer.queryFeatures(queryParams);
      const updatedFeatures = features.map((feature) => {
        const Value = getAdmin1Value(feature, indicatorsObject);

        feature.attributes.Value = Value;
        return feature;
      });

      await this.admin1ClientSideFeaturesLayer
        .applyEdits({
          updateFeatures: updatedFeatures,
        })
        .then((response) => {
          console.log('updateFeatures:', response);
        })
        .catch((error) => {
          console.error('updateFeatures error:', error);
        });

      this.admin1ClientSideFeaturesLayer.renderer = await getAdminClassBreaksRenderer(
        this.admin1ClientSideFeaturesLayer
      );

      // Update Admin1 FeatureLayer definitionExpression
      const { levelSelected } = store.getState().admin2EstimatesSlice;
      const definitionExpression = getAdmin1DefinitionExpressionValue(levelSelected);
      this.admin1ClientSideFeaturesLayer.definitionExpression = definitionExpression;
    }
  }

  async updateAdmin2Graphic(
    admin2FeaturesData: any,
    indicatorID: string,
    surveySelected: SurveySelectedType | null,
    updateFeatures = false
  ) {
    if (this.map && this.mapView && this.admin2ClientSideFeaturesLayer) {
      const { levelSelected } = store.getState().admin2EstimatesSlice;
      const queryParams = { where: '1=1' };
      const admin2ClientSideFeaturesLayerSourceItems = this.admin2ClientSideFeaturesLayer.source.items;
      const { features } = await this.admin2ClientSideFeaturesLayer.queryFeatures(queryParams);
      let featuresUpdate: any[] = [];
      let featuresAdd: any[] = [];

      if (updateFeatures) {
        const admin2FeaturesDataFiltered = getAdmin2FeaturesDataFiltered(
          admin2FeaturesData,
          indicatorID,
          surveySelected
        );
        admin2ClientSideFeaturesLayerSourceItems.forEach((feature: Graphic) => {
          const { admin2FeaturesAttributes } = getAdmin2FeaturesAttributes(admin2FeaturesDataFiltered, feature);
          const admin2AddedAttributes = getAdmin2AddedAttributes(admin2FeaturesAttributes);
          feature.attributes = { ...feature.attributes, ...admin2AddedAttributes };
          featuresUpdate.push(feature);
        });
      } else {
        if (admin2ClientSideFeaturesLayerSourceItems.length !== features.length) {
          if (levelSelected.level !== LEVEL_ALL.level) {
            admin2ClientSideFeaturesLayerSourceItems.forEach((feature: Graphic) => {
              if (levelSelected.admin1RegionID === feature.attributes.region_id) {
                featuresAdd.push(feature);
              }
            });
          } else {
            const featuresRegionIDs = features.map((feature: Graphic) => {
              return feature.attributes.region_id;
            });
            admin2ClientSideFeaturesLayerSourceItems.forEach((feature: Graphic) => {
              if (!featuresRegionIDs.includes(feature.attributes.region_id)) {
                featuresAdd.push(feature);
              }
            });
          }
        }
      }

      let featuresDelete: any[] = [];
      if (levelSelected.level !== LEVEL_ALL.level) {
        featuresDelete = features
          .filter((feature: Graphic) => {
            return levelSelected.admin1RegionID !== feature.attributes.region_id;
          })
          .map((feature: Graphic) => {
            return { objectId: feature.attributes.OBJECTID };
          });
      }

      await this.admin2ClientSideFeaturesLayer
        .applyEdits({
          updateFeatures: featuresUpdate,
          addFeatures: featuresAdd,
          deleteFeatures: featuresDelete,
        })
        .then((response) => {
          console.log('updateFeatures:', response);
        })
        .catch((error) => {
          console.error('updateFeatures error:', error);
        });

      this.admin2ClientSideFeaturesLayer.renderer = await getAdminClassBreaksRenderer(
        this.admin2ClientSideFeaturesLayer
      );

      // Update Admin2 FeatureLayer definitionExpression
      const definitionExpression = getAdmin2DefinitionExpressionValue(levelSelected);
      this.admin2ClientSideFeaturesLayer.definitionExpression = definitionExpression;
    }
  }

  async updateAdminRenderer(mapLegendDomRef: any, adminSelected: string) {
    if (this.map && this.mapView) {
      const currentAdminFeatureLayer =
        ADMIN__Admin1 === adminSelected ? this.admin1ClientSideFeaturesLayer : this.admin2ClientSideFeaturesLayer;

      if (currentAdminFeatureLayer) {
        const { classBreakInfos } = await getAdminClassBreaksRenderer(currentAdminFeatureLayer);

        // Update FeatureLayer renderer
        currentAdminFeatureLayer?.renderer.set({ classBreakInfos });

        // Remove Legend from DOM because dynamic FeatureLayer renderer does not update the legend
        mapLegendDomRef.current.removeChild(mapLegendDomRef.current.childNodes[0]);

        // Add Legend to DOM
        this.legend = new Legend({
          view: this.mapView,
          container: mapLegendDomRef.current,
        });
      }
    }
  }

  handleAdmin1ClientSideFeaturesLayerVisibility(adminSelected: string) {
    const layerVisibility = ADMIN__Admin1 === adminSelected;
    if (this.map && this.mapView && this.admin1ClientSideFeaturesLayer) {
      this.admin1ClientSideFeaturesLayer.visible = layerVisibility;
    }
  }

  addAdmin2ReferenceFeatureLayer(countryName: string) {
    this.admin2ReferenceFeatureLayer = getAdmin2ReferenceFeatureLayer(countryName);
    this.map?.add(this.admin2ReferenceFeatureLayer);
  }

  async updateAdmin2ReferenceFeatureLayer(countryName: string, surveySelected: SurveySelectedType) {
    if (this.admin2ReferenceFeatureLayer) {
      this.admin2ReferenceFeatureLayer.definitionExpression = `ADM0_NAME = '${countryName}'`;
      reorderAdmin2ReferenceFeatureLayerToTop();
    }

    if (this.admin2BoundariesGraphicsLayer && !this.admin2BoundariesGraphicsLayer?.id.endsWith(countryName)) {
      const boundariesGraphics = await getAdmin2BoundariesGraphics(surveySelected);
      await handleAdminBoundariesGraphics(
        this.admin2BoundariesGraphicsLayer,
        boundariesGraphics,
        surveySelected,
        ADMIN2_BOUNDARIES_LAYER_ID
      );
    }
  }

  handleAdmin2ReferenceLayerVisibility(adminSelected: string) {
    const layerVisibility = ADMIN__5X5km === adminSelected;
    if (this.map && this.mapView && this.admin2ReferenceFeatureLayer) {
      this.admin2ReferenceFeatureLayer.visible = layerVisibility;
    }
  }

  handleAdmin2ClientSideFeaturesLayerVisibility(adminSelected: string) {
    const layerVisibility = ADMIN__Admin2 === adminSelected;
    if (this.map && this.mapView && this.admin2ClientSideFeaturesLayer) {
      this.admin2ClientSideFeaturesLayer.visible = layerVisibility;
    }
  }

  handleCountryMapImageLayerVisibility(
    adminSelected: string,
    indicatorSelected: IndicatorType,
    surveySelected: SurveySelectedType | null,
    fieldSelected: string
  ) {
    if (this.map && this.mapView) {
      if (adminSelected === ADMIN__5X5km && surveySelected) {
        const indicatorID = indicatorSelected.value;
        const countryCode = surveySelected?.DHSCC;

        const isDifferentCountry = this.countryMapImageLayer?.title !== countryCode;
        if (!this.countryMapImageLayer || isDifferentCountry) {
          const countryMapImageLayer = this.create_5x5_GroupLayer(surveySelected);
          countryMapImageLayer.when(() => {
            countryMapImageLayer.visible = true;
            this.update__5x5__SelectedSubLayer(surveySelected, indicatorID, fieldSelected);
            reorderAdminBoundariesLayerToTop(this.admin2BoundariesGraphicsLayer);
          });

          if (isDifferentCountry) {
            const countryName = surveySelected?.ADM0;
            this.updateAdmin2ReferenceFeatureLayer(countryName, surveySelected);
          }
        } else {
          this.countryMapImageLayer.visible = true;
          this.update__5x5__SelectedSubLayer(surveySelected, indicatorID, fieldSelected);
        }
      } else {
        if (this.countryMapImageLayer) {
          this.countryMapImageLayer.visible = false;
        }
      }
    }
  }

  update__5x5__SelectedSubLayer(surveySelected: SurveySelectedType, indicatorID: string, fieldSelected: string) {
    let subLayerID = getSelectedSubLayerID(surveySelected, indicatorID, fieldSelected);
    // TODO: This is a temporary fix to handle the fact that the indicator value is not the same as the field name in the GIS REST API
    const subLayerIdBackup = getSelectedSubLayerID(
      surveySelected,
      NO_IMPROVED_WATER_PREMISES_FIELD_VALUE_BACKUP,
      fieldSelected
    );

    if (this.countryMapImageLayer) {
      this.countryMapImageLayer?.allSublayers?.items.forEach((sublayer: any) => {
        const title = sublayer.title.split('.')[0];

        // TODO: This is a temporary fix to handle the fact that the indicator value is not the same as the field name in the GIS REST API
        if (subLayerID === subLayerIdBackup) {
          subLayerID = getSelectedSubLayerID(surveySelected, NO_IMPROVED_WATER_PREMISES_FIELD_VALUE, fieldSelected);
        }

        if (title === subLayerID) {
          sublayer.visible = true;
        } else {
          sublayer.visible = false;
        }
      });
    }
  }

  handleMapImageLayerClippingEffect = (boundaryFeature: __esri.Graphic | undefined) => {
    if (boundaryFeature && this.effectsGraphicsLayer && this.effectsGroupLayer) {
      this.effectsGraphicsLayer?.removeAll();
      this.effectsGraphicsLayer?.add(boundaryFeature);
      this.effectsGroupLayer.effect = 'brightness(1.5) drop-shadow(0, 0, 12px)';
    }
  };

  handleAdmin1Update(adminSelected: string, levelSelected: LevelSelectedType) {
    const queryParams = {
      where: '1=1',
      returnGeometry: true,
    };

    this.admin1ClientSideFeaturesLayer?.queryFeatures(queryParams).then((response) => {
      if (levelSelected.level === LEVEL_ALL.level) {
        if (!this.mapView?.animation) {
          this.handlePopupMarkerGraphicsLayer();
          this.mapView?.goTo(this.admin1ClientSideFeaturesLayer?.fullExtent, GO_TO_OPTIONS);
        }
      } else {
        const features = response.features;
        const feature: Graphic | undefined = features.find((feature) => {
          const featureAttributeAdmin1RegionIdFieldValue = feature.attributes[ADMIN1_REGION_ID_FIELD];
          const isFeatureAttributeMatching = featureAttributeAdmin1RegionIdFieldValue === levelSelected.admin1RegionID;
          return isFeatureAttributeMatching;
        });

        if (feature) {
          if (!this.mapView?.animation) {
            this.handlePopupMarkerGraphicsLayer(adminSelected, feature);
            this.mapView?.goTo(feature, GO_TO_OPTIONS);
          }
        } else {
          console.error('No Admin 1 feature found for level selected:', levelSelected);
          console.error(
            'Present level DHSREGEN:',
            features.map((feature) => feature.attributes[DHSREGEN])
          );
        }
      }
    });

    updateAdmin1ClientSideFeaturesLayerDefinitionExpression(levelSelected);
    updateAdmin2ClientSideFeaturesLayerDefinitionExpression(levelSelected);
  }

  handlePopupMarkerGraphicsLayer = (adminSelected?: string, feature?: Graphic | undefined) => {
    if (this.popupMarkerGraphicsLayer?.graphics?.items?.length > 0) {
      this.popupMarkerGraphicsLayer?.graphics.removeAll();
    }

    if (adminSelected === ADMIN__5X5km) {
      const region = {
        type: 'simple-fill',
        color: HIGHLIGHT_COLOR_OPACITY,
        outline: {
          width: 2,
          color: HIGHLIGHT_COLOR,
        },
      };

      if (!feature) {
        const { levelSelected } = store.getState().admin2EstimatesSlice;
        const { admin1RegionID } = levelSelected;

        feature = this.admin1ClientSideFeaturesLayer?.source.items.find((feature: any) => {
          return feature.attributes[ADMIN1_REGION_ID_FIELD] === admin1RegionID;
        });
      }

      const graphic = new Graphic({
        geometry: feature?.geometry,
        symbol: region,
      });

      this.popupMarkerGraphicsLayer?.add(graphic);
      reorderPopupMarkerGraphicsLayerToTop();
    }
  };
}

const mapController = new MapController();
export default mapController;

declare global {
  interface Window {
    mapController: typeof mapController;
    store: typeof store;
  }
}

if (import.meta.env.MODE === 'development') {
  window.mapController = mapController;
  window.store = store;
}
